/*
 * color-swatches.js
 * Single source of truth for resolving color names → hex across the theme.
 *
 * Any element with `data-color-name="<value>"` will have its background
 * resolved here. Resolution order:
 *   1. If element already has an inline background-color / background-image set
 *      by Liquid (e.g. Shopify native swatch, brand-tuned map, gradient), it is
 *      left alone.
 *   2. Look up the lowercased + trimmed name in COLOR_HEX_MAP.
 *   3. If still unresolved, treat the literal name as a CSS color (handles
 *      'maroon', 'gold', 'tomato', etc.).
 *   4. If still unresolved, fall back to api.color.pizza so any color name
 *      Shopify accepts will eventually load.
 *
 * Exposes window.UntamedColorSwatches = { map, resolve, hydrate }.
 */
(function () {
  'use strict';

  var COLOR_HEX_MAP = {
    // Brand-tuned (mirrors product page color map for visual parity)
    'amber': '#b06d20',
    'black': '#1a1a1a',
    'white': '#ffffff',
    'forest': '#1f473e', 'forest green': '#1f473e',
    'moss green': '#5a6b3b', 'moss': '#5a6b3b',
    'sage': '#9caf88',
    'olive': '#6b7233',
    'khaki': '#b5a642',
    'camo': '#78866b',
    'charcoal': '#36393d',
    'slate': '#5a6470',
    'gray': '#8a8a8a', 'grey': '#8a8a8a',
    'silver': '#c0c0c0',
    'stone': '#a8a196',
    'taupe': '#8b7e74',
    'rust': '#9c4521',
    'coral': '#ff7f50',
    'red': '#c0392b',
    'burgundy': '#5c1a2b', 'maroon': '#5c1a2b', 'wine': '#5c1a2b',
    'mauve': '#b784a7',
    'dawn pink': '#e8c4c0', 'pink': '#e8c4c0', 'blush': '#e8c4c0',
    'navy': '#1f2a4d',
    'indigo': '#3f51b5',
    'blue': '#2b50aa',
    'deep sea': '#1b4a5a', 'teal': '#1b4a5a',
    'beige': '#e0d5bd',
    'sand': '#d8c4a0',
    'cream': '#f2efe6', 'ivory': '#f2efe6',
    'gold': '#c9a227',
    'mustard': '#d4a017',
    'tan': '#c19a6b', 'camel': '#c19a6b',
    'walnut': '#4b3621', 'dark brown': '#4b3621', 'brown': '#4b3621', 'chocolate': '#4b3621',
    // Extended common names
    'yellow': '#eab308', 'orange': '#ea580c', 'green': '#16a34a', 'purple': '#9333ea',
    'lavender': '#e6e6fa', 'lilac': '#c8a2c8', 'mint': '#98fb98', 'emerald': '#50c878',
    'jade': '#00a36c', 'ash': '#b2beb5', 'copper': '#b87333', 'bronze': '#cd7f32',
    'champagne': '#f7e7ce', 'espresso': '#3c1414', 'mocha': '#967969', 'coffee': '#6f4e37',
    'caramel': '#ffd59a', 'peach': '#ffcba4', 'apricot': '#fbceb1', 'rose': '#ff007f',
    'fuchsia': '#ff00ff', 'magenta': '#ff00ff', 'berry': '#8e4585', 'plum': '#8e4585',
    'cherry': '#de3163', 'ruby': '#e0115f', 'crimson': '#dc143c', 'scarlet': '#ff2400',
    'tomato': '#ff6347', 'brick': '#cb4154', 'clay': '#b66a50', 'terracotta': '#e2725b',
    'cinnamon': '#d2691e', 'lemon': '#fff44f', 'lime': '#32cd32', 'chartreuse': '#7fff00',
    'pine': '#01796f', 'hunter': '#355e3b', 'seafoam': '#93e9be', 'aqua': '#00ffff',
    'cyan': '#00ffff', 'turquoise': '#40e0d0', 'sky': '#87ceeb', 'cobalt': '#0047ab',
    'royal': '#4169e1', 'midnight': '#191970', 'denim': '#1560bd', 'periwinkle': '#ccccff',
    'violet': '#7f00ff', 'orchid': '#da70d6', 'dusty rose': '#dcae96', 'baby blue': '#89cff0',
    'powder blue': '#b0e0e6', 'light blue': '#add8e6', 'dark blue': '#00008b',
    'light green': '#90ee90', 'dark green': '#006400', 'light gray': '#d3d3d3',
    'light grey': '#d3d3d3', 'dark gray': '#404040', 'dark grey': '#404040',
    'off white': '#faf9f6', 'off-white': '#faf9f6', 'natural': '#efe4d2',
    'oatmeal': '#d8c8a8', 'pewter': '#8e9091', 'graphite': '#383838', 'onyx': '#353839',
    'jet': '#343434', 'snow': '#fffafa', 'pearl': '#eae6ca', 'bone': '#e3dac9',
    'wheat': '#f5deb3', 'ecru': '#c2b280'
  };

  // Gradients for two-tone colors (matches product page).
  var COLOR_GRADIENT_MAP = {
    'camo': 'linear-gradient(135deg,#78866b 0 33%,#4b5320 33% 66%,#3b3b2f 66% 100%)',
    'gold/cream': 'linear-gradient(135deg,#c9a227 0 50%,#f2efe6 50% 100%)',
    'silver/black': 'linear-gradient(135deg,#c0c0c0 0 50%,#1a1a1a 50% 100%)'
  };

  function normalize(name) {
    return String(name || '').trim().toLowerCase();
  }

  function resolve(name) {
    var n = normalize(name);
    if (!n) return '';
    if (COLOR_HEX_MAP[n]) return COLOR_HEX_MAP[n];
    // Try without spaces (e.g. "off-white" / "off white" / "offwhite")
    var alt = n.replace(/\s+/g, '');
    if (COLOR_HEX_MAP[alt]) return COLOR_HEX_MAP[alt];
    // Literal CSS color fall-through (e.g. "tomato", "gold", "maroon")
    if (/^[a-z]+$/.test(alt)) return alt;
    return '';
  }

  function hasInlineBackground(el) {
    if (!el || !el.style) return false;
    return Boolean(el.style.backgroundColor || el.style.backgroundImage);
  }

  function apply(el, value) {
    if (!el) return;
    var grad = COLOR_GRADIENT_MAP[normalize(el.getAttribute('data-color-name'))];
    if (grad) {
      el.style.backgroundImage = grad;
      return;
    }
    el.style.backgroundColor = value;
  }

  var pizzaInflight = {};

  function fetchFromPizza(name, els) {
    if (pizzaInflight[name]) {
      pizzaInflight[name].push.apply(pizzaInflight[name], els);
      return;
    }
    pizzaInflight[name] = els.slice();
    var encoded = encodeURIComponent(name);
    fetch('https://api.color.pizza/v1/names/?name=' + encoded + '&list=default')
      .then(function (r) { return r.ok ? r.json() : Promise.reject(); })
      .then(function (data) {
        var colors = data && data.colors;
        if (!colors || !colors.length) return;
        var hex = '#' + String(colors[0].hex || '').replace('#', '');
        COLOR_HEX_MAP[name] = hex;
        (pizzaInflight[name] || []).forEach(function (el) { apply(el, hex); });
      })
      .catch(function () {})
      .then(function () { delete pizzaInflight[name]; });
  }

  function hydrate(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-color-name]');
    var unknown = {};
    Array.prototype.forEach.call(nodes, function (el) {
      if (hasInlineBackground(el)) return;
      var name = normalize(el.getAttribute('data-color-name'));
      if (!name) return;
      var hex = resolve(name);
      if (hex) { apply(el, hex); return; }
      if (!unknown[name]) unknown[name] = [];
      unknown[name].push(el);
    });
    Object.keys(unknown).forEach(function (name) {
      fetchFromPizza(name, unknown[name]);
    });
  }

  window.UntamedColorSwatches = {
    map: COLOR_HEX_MAP,
    gradients: COLOR_GRADIENT_MAP,
    resolve: resolve,
    hydrate: hydrate,
    normalize: normalize
  };

  function init() { hydrate(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function (e) {
    hydrate((e && e.target) || document);
  });

  var debounceTimer = null;
  var observer = new MutationObserver(function (mutations) {
    var added = false;
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes && mutations[i].addedNodes.length) { added = true; break; }
    }
    if (!added) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () { hydrate(document); }, 150);
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
